import { createContext, PropsWithChildren, useCallback, useEffect, useMemo, useRef, useState } from "react";

interface UndoRedoProviderProps<S> extends PropsWithChildren {
  initialState?: S;
}

export type HistoryEventType = "undo" | "redo" | "set" | null;

export interface HistorySnapshot<S> {
  state: S;
  eventType: HistoryEventType;
}

export interface SetOptions {
  silent?: boolean;
}

export interface HistoryState<S> {
  state: S;
  set: (newState: S, options?: SetOptions) => void;
  undo: () => void;
  redo: () => void;
  clear: () => void;
  canUndo: boolean;
  canRedo: boolean;
}

export type ExtendedHistoryState<S> = HistoryState<S> & {
  subscribe: (callback: () => void) => () => void;
  getSnapshot: () => HistorySnapshot<S>;
};

export const HistoryContext = createContext<ExtendedHistoryState<any>>(undefined!);

const HistoryProvider = <S,>({ children, initialState }: UndoRedoProviderProps<S>): JSX.Element => {
  const [undoStack, setUndoStack] = useState<S[]>(initialState ? [initialState] : []);
  const [redoStack, setRedoStack] = useState<S[]>([]);
  const [currentState, setCurrentState] = useState<S>(initialState as S);

  const listeners = useRef<Set<() => void>>(new Set());
  const lastEventType = useRef<HistoryEventType>(null);
  const snapshotRef = useRef<HistorySnapshot<S>>({ state: currentState, eventType: null });

  // Update snapshot whenever currentState changes AND notify listeners
  useEffect(() => {
    snapshotRef.current = {
      state: currentState,
      eventType: lastEventType.current,
    };

    // Only notify listeners if there's an event type (meaning an action was taken)
    if (lastEventType.current) {
      listeners.current.forEach((listener) => listener());
    }

    // Reset event type after snapshot is updated and listeners notified
    lastEventType.current = null;
  }, [currentState]);

  // Subscribe function for useSyncExternalStore
  const subscribe = useCallback((callback: () => void) => {
    listeners.current.add(callback);
    return () => {
      listeners.current.delete(callback);
    };
  }, []);

  // Get current snapshot
  const getSnapshot = useCallback((): HistorySnapshot<S> => {
    return snapshotRef.current;
  }, []);

  // Set with optional silent mode
  const set = useCallback((newState: S, options: SetOptions = {}) => {
    const { silent = false } = options;

    if (silent) {
      // Silent mode: amend the current state without creating new history entry
      setUndoStack((prev) => {
        if (prev.length === 0) return prev;
        const newStack = [...prev];
        newStack[newStack.length - 1] = newState;
        return newStack;
      });
      // Update current state but don't set event type (no listener notification)
      setCurrentState(newState);
    } else {
      // Normal mode: create new history entry
      lastEventType.current = "set";
      setUndoStack((prev) => [...prev, newState]);
      setRedoStack([]);
      setCurrentState(newState);
    }
  }, []);

  // Undo to previous state
  const undo = useCallback(() => {
    setUndoStack((prev) => {
      if (prev.length <= 1) return prev;

      const newStack = [...prev];
      const stateToMove = newStack.pop()!;

      setRedoStack((redoPrev) => [...redoPrev, stateToMove]);
      const previousState = newStack[newStack.length - 1];

      lastEventType.current = "undo";
      setCurrentState(previousState);

      return newStack;
    });
  }, []);

  // Redo to next state
  const redo = useCallback(() => {
    setRedoStack((prev) => {
      if (prev.length === 0) return prev;

      const newStack = [...prev];
      const nextState = newStack.pop()!;

      setUndoStack((undoPrev) => [...undoPrev, nextState]);

      lastEventType.current = "redo";
      setCurrentState(nextState);

      return newStack;
    });
  }, []);

  // Clear history and reset to initial state
  const clear = useCallback(() => {
    setUndoStack(initialState ? [initialState] : []);
    setRedoStack([]);
    setCurrentState(initialState as S);
    lastEventType.current = null;
  }, [initialState]);

  const canUndo = undoStack.length > 1;
  const canRedo = redoStack.length > 0;

  const extendedHistory: ExtendedHistoryState<S> = useMemo(
    () => ({
      state: currentState,
      set,
      undo,
      redo,
      clear,
      canUndo,
      canRedo,
      subscribe,
      getSnapshot,
    }),
    [currentState, set, undo, redo, clear, canUndo, canRedo, subscribe, getSnapshot]
  );

  return <HistoryContext.Provider value={extendedHistory}>{children}</HistoryContext.Provider>;
};

export default HistoryProvider;
